import { Layers, type Object3D, type Scene } from "three";

/**
 * Layer used by the TSL AO gbuffer pass. Contributing meshes enable this bit;
 * Lines / Points / `contributeToAO === "false"` leave it disabled.
 * Beauty pass keeps the default full mask, so those objects still render.
 */
export const AO_CONTRIBUTION_LAYER = 2;

/**
 * Same exclusion rules as classic GTAOPass / AccumulatingGTAOPass.
 */
export function shouldExcludeFromAo(object: Object3D): boolean {
  const typed = object as Object3D & {
    isPoints?: boolean;
    isLine?: boolean;
  };
  return (
    typed.isPoints === true ||
    typed.isLine === true ||
    object.userData.contributeToAO === "false"
  );
}

/** Overlay helpers (bbox meters, replace boxes): no pick, no GTAO / AO gbuffer. */
export function excludeFromSelectAndAo(object: Object3D): void {
  object.userData.contributeToAO = "false";
  object.raycast = () => {};
}

export function excludeSubtreeFromSelectAndAo(object: Object3D): void {
  object.traverse((child) => excludeFromSelectAndAo(child));
}

/** Layers mask for PassNode.setLayers — only AO-contributing objects. */
export function createAoContributionLayers(): Layers {
  const layers = new Layers();
  layers.set(AO_CONTRIBUTION_LAYER);
  return layers;
}

/**
 * Sync AO contribution layer bits without touching `.visible`.
 * Safe to call before the AO gbuffer pass; beauty is unaffected.
 */
export function syncAoContributionLayers(scene: Scene): void {
  scene.traverse((object) => {
    if (shouldExcludeFromAo(object)) {
      object.layers.disable(AO_CONTRIBUTION_LAYER);
    } else {
      object.layers.enable(AO_CONTRIBUTION_LAYER);
    }
  });
}

/** Classic WebGL GTAO gbuffer: temporarily hide non-contributors. */
export function overrideAoVisibility(
  scene: Scene,
  cache: Map<Object3D, boolean>,
): void {
  scene.traverse((object) => {
    cache.set(object, object.visible);
    if (shouldExcludeFromAo(object)) {
      object.visible = false;
    }
  });
}

export function restoreAoVisibility(
  scene: Scene,
  cache: Map<Object3D, boolean>,
): void {
  scene.traverse((object) => {
    const visible = cache.get(object);
    if (visible !== undefined) {
      object.visible = visible;
    }
  });
  cache.clear();
}
